import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { MdVisibility, MdWarningAmber } from 'react-icons/md';
import { FaBan } from 'react-icons/fa';
import { Modal, Button } from 'components/common';
import apiAxios from 'helpers/apiAxios';
import { startImpersonation } from 'helpers/impersonation';
import resetPersistedState from 'helpers/resetPersistedState';
import { persistor } from 'redux/store';

/**
 * Starts an administrator working as another user, without that user's password.
 *
 * The reason field is required on purpose. Impersonation is the replacement for
 * administrators borrowing a colleague's password, and the thing that made that
 * practice unacceptable was that nothing recorded WHY. A free-text line costs
 * five seconds and turns the audit entry from "an admin opened this account"
 * into something a person can actually review later.
 */
const ImpersonateModal = ({ user, open, setOpen }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const close = () => {
    setReason('');
    setOpen(false);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving || reason.trim().length < 5) return;

    setSaving(true);
    try {
      const response = await apiAxios.post('/authorization/user_sessions/impersonate', {
        user_id: user.id,
        reason: reason.trim(),
      });

      const payload = response.data?.data;
      if (!payload?.access_token) throw new Error('The server did not return a session.');

      startImpersonation({
        tokens: payload,
        target: { id: user.id, name: user.name, email: user.email },
      });

      // A full reload rather than a redux update: every slice in the store holds
      // data fetched as the administrator, and leaving any of it behind would
      // show one person's records under another person's name. The reload alone
      // rehydrated the persisted store, so it is dropped first.
      await resetPersistedState(persistor);
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Could not start the session.');
      setSaving(false);
    }
  };

  return (
    <Modal
      show={open}
      onHide={close}
      title={`View as ${user?.name}`}
      backdropClick={false}
      footer={(
        <>
          <Button size="sm" type="submit" form="impersonate-form" disabled={saving || reason.trim().length < 5}>
            <MdVisibility size={15} /> {saving ? 'Starting…' : 'Start viewing'}
          </Button>
          <Button size="sm" variant="danger" onClick={close} disabled={saving}>
            <FaBan /> Cancel
          </Button>
        </>
      )}
    >
      <form id="impersonate-form" onSubmit={submit}>
        <div className="tw-flex tw-items-start tw-gap-2.5 tw-mb-4 tw-px-3 tw-py-2.5 tw-rounded tw-bg-amber-50 tw-border tw-border-solid tw-border-amber-200">
          <MdWarningAmber size={17} className="tw-text-amber-500 tw-shrink-0 tw-mt-0.5" />
          <div className="tw-text-xs tw-text-amber-800">
            <p className="tw-m-0 tw-mb-1">
              You will see the portal exactly as <strong>{user?.name}</strong> does, and
              anything you do will be recorded against your name as well as theirs.
            </p>
            <p className="tw-m-0">
              The session ends after two hours, or when
              you press <strong>Stop</strong> in the banner at the top of the screen.
            </p>
          </div>
        </div>

        <label htmlFor="impersonate-reason" className="tw-block tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-slate-400 tw-mb-1.5">
          Why do you need to do this?
        </label>
        <input
          id="impersonate-reason"
          type="text"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="e.g. Checking a quotation they say is missing"
          autoFocus
          className="tw-w-full tw-h-9 tw-px-3 tw-text-sm tw-rounded-md tw-border tw-border-solid tw-border-slate-200 focus:tw-border-primary-400 focus:tw-outline-none"
        />
        <p className="tw-text-xs tw-text-slate-400 tw-m-0 tw-mt-1">
          Stored on the audit record and shown to {user?.name}.
        </p>
      </form>
    </Modal>
  );
};

ImpersonateModal.propTypes = {
  user: PropTypes.object,
  open: PropTypes.bool,
  setOpen: PropTypes.func.isRequired,
};

export default ImpersonateModal;
